<template>
  <div>
    <FlotoForm layout="vertical" class="flex items-center">
      <div class="flex w-full">
        <MInputGroup compact class="input-group-container">
          <div class="flex-1">
            <MInputNumber
              id="day-time-picker-input"
              v-model="textValue"
              :disabled="disabled"
              :precision="0"
              :min="0"
              :max="maxValue"
              :placeholder="$tc(currentType, 3)"
            />
          </div>
          <MSelect
            v-if="durationOptions.length > 1 || allowedUnits.length"
            v-model="currentType"
            :placeholder="$t('select')"
            :options="durationOptions"
            as-input
            :disabled="durationOptions.length === 1"
            :dropdown-match-select-width="false"
            :allow-clear="false"
            class="duration-typess"
            :class="dayTimeTriggerStyle"
            input-classes="rounded-tl-none rounded-bl-none"
            @change="handleTimeUnitChange"
          >
          </MSelect>
        </MInputGroup>
      </div>
      <template v-slot:submit>
        <span />
      </template>
    </FlotoForm>
  </div>
</template>

<script>
import Moment from 'moment'

export default {
  name: 'FlotoDaytimePicker',
  model: {
    event: 'change',
  },
  props: {
    disabled: { type: Boolean, default: false },
    value: { type: Number, default: 0 },
    dayTimeTriggerStyle: {
      type: Object,
      default() {
        return {}
      },
    },
    allowedUnits: {
      type: Array,
      default() {
        return []
      },
    },
    maxDays: {
      type: Number,
      default: 365,
    },
  },
  data() {
    return {
      textValue: undefined,
      currentType: (this.allowedUnits || []).length
        ? this.allowedUnits[0]
        : 'hours',
      timeUnit: 0,
      isUnitChanging: false, // Track if unit is being changed to prevent textValue update
    }
  },
  computed: {
    maxValue() {
      if (this.currentType === 'hours') {
        return 720
      }
      if (this.currentType === 'days') {
        return this.maxDays
      }
      if (this.currentType === 'minutes') {
        return 43200
      }
      if (this.currentType === 'months') {
        return 12
      }
      return undefined
    },
    durationOptions() {
      let allOptions = [
        { text: this.$tc('hours', 3), value: 'hours', key: 'hours' },
        { text: this.$tc('days', 3), value: 'days', key: 'days' },
        { text: this.$tc('minutes', 3), value: 'minutes', key: 'minutes' },
        { text: this.$tc('months', 3), value: 'months', key: 'months' },
      ]
      if (this.allowedUnits.length) {
        allOptions = allOptions.filter(
          ({ value }) => this.allowedUnits.indexOf(value) >= 0
        )
      }
      return allOptions
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        // Don't update textValue if unit is being changed
        if (!this.isUnitChanging) {
          // If the new value matches value of the current textValue, don't re-convert
          if (
            this.textValue !== undefined &&
            this.textValue !== null &&
            this.currentType
          ) {
            const currentVal = parseInt(this.textValue)
            if (!isNaN(currentVal)) {
              const currentInputMs = Moment.duration(
                currentVal,
                this.currentType
              ).asMilliseconds()
              // If incoming value is effectively the same as what inputs represent, skip
              if (Math.abs(newValue - currentInputMs) < 100) {
                return
              }
            }
          }
          this.textValue = this.convertValueForText()
        }
      },
    },
    textValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$nextTick(() => this.update())
      }
    },
  },
  mounted() {
    this.isUnitChanging = false
  },
  updated() {
    this.isUnitChanging = false
    this.timeUnit = `${this.textValue} ${this.$tc(this.currentType, 3)}`
    this.$emit('descriptionValue', this.timeUnit)
  },
  methods: {
    setTimeUnit(unit) {
      this.currentType = unit
      this.isUnitChanging = true
      this.$nextTick(() => {
        this.update()
      })
    },
    handleTimeUnitChange($event) {
      this.isUnitChanging = true
      this.$emit('timeUnit', $event)
      this.$nextTick(() => {
        this.update()
      })
    },
    formatedValue(value) {
      return value && value >= 0 ? +value : 0
    },
    update() {
      const v = parseInt(this.textValue)
      if (v === 0 || isNaN(v)) {
        this.$emit('change', 0)
      } else {
        const m = Moment.duration(v, this.currentType)
        this.$emit('change', m.asMilliseconds())
      }
    },
    convertValueForText() {
      const formatedValue = this.formatedValue(this.value)
      if (formatedValue) {
        const m = Moment.duration(formatedValue, 'milliseconds')
        const hierarchy = ['months', 'days', 'hours', 'minutes']

        // Helper to check if a value is effectively an integer
        const isInteger = (val) => Math.abs(val - Math.round(val)) < 0.02

        for (const unit of hierarchy) {
          // If allowedUnits is set, restrict to only those units
          if (this.allowedUnits.length && !this.allowedUnits.includes(unit)) {
            continue
          }

          const val = m.as(unit)
          // strict check: value must be effectively an integer and at least 1 (to avoid rounding 0.9 to 1 if we want to be strict, but here roughly >=1 is fine)
          if (Math.round(val) >= 1 && isInteger(val)) {
            this.currentType = unit
            return Math.round(val)
          }
        }

        // Fallback: If no integer match found in hierarchy
        let fallbackUnit = 'minutes'
        if (this.allowedUnits.length) {
          if (this.allowedUnits.includes('minutes')) {
            fallbackUnit = 'minutes'
          } else if (this.allowedUnits.includes('hours')) {
            fallbackUnit = 'hours'
          } else {
            // Pick the smallest granular unit available (last in allowed list assuming they might be ordered, or just safe fallback)
            // Ideally we'd pick the one that gives best precision, but simple fallback is safe.
            fallbackUnit = this.allowedUnits[this.allowedUnits.length - 1]
          }
        }

        this.currentType = fallbackUnit
        // Return rounded value for the fallback unit to ensure display is clean
        return Math.round(m.as(fallbackUnit))
      }
    },
  },
}
</script>

<style lang="less" scoped>
.input-group-container {
  top: 0 !important;
  display: flex !important;
  .@{ant-prefix}-input-number {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>

<style lang="less">
.input-group-container {
  .duration-type {
    .@{ant-prefix}-select-selection {
      color: var(--page-text-color) !important;
      background-color: var(--page-background-color) !important;
      border-color: var(--border-color);
    }
  }
}
.@{ant-prefix}-select-dropdown-menu-item {
  background-color: var(--dropdown-background);
}
.@{ant-prefix}-select-dropdown-menu-item:hover {
  color: var(--primary);
  background-color: var(--dropdown-hover-bg) !important;
}
</style>
